import { AutomationActionType, AutomationEdge, AutomationNode, AutomationNodeData } from '../../../types/automation';

/**
 * Chain and visual graph helpers for the ISATS visual automation canvas.
 *
 * Facilitates 2D node graph interactions:
 * - Sequential execution topological order computation.
 * - Drag-to-connect validation and edge re-chaining.
 * - Connection removal without node deletion.
 * - Automatic 2D arrangement and node re-alignment.
 * - Node duplication and smart deletion-reconnection.
 * - Dynamic configuration summary string formatting.
 */

/** Horizontal & vertical spacing constants for 2D auto-layout. */
export const CANVAS_NODE_X_ORIGIN = 100;
export const CANVAS_NODE_X_STEP = 280;
export const CANVAS_NODE_Y_DEFAULT = 180;

/** Card geometry on the canvas (mirrors AutomationCanvas NODE_WIDTH/NODE_HEIGHT). */
export const CANVAS_NODE_WIDTH = 250;
export const CANVAS_NODE_HEIGHT = 120;
/**
 * Lane snapping for Auto Align: while sweeping nodes by current y, a node
 * joins the open lane while its top is within the card height plus this
 * tolerance OF THE LANE'S BASELINE (the lane's minimum y) — the budget is
 * spent once, from the baseline, never re-spent per step. (The previous
 * rule measured against the lane's growing maximum, so a staircase of
 * nodes each within tolerance of the previous one chain-crept the lane
 * arbitrarily far down and flattened a node hundreds of units below the
 * baseline onto the main row.) Snapped lanes sit at least
 * CANVAS_NODE_HEIGHT + CANVAS_LANE_SNAP_TOLERANCE apart, so re-running
 * Auto Align re-forms the identical lanes (fixed point) and cards in
 * different lanes can never overlap vertically.
 */
export const CANVAS_LANE_SNAP_TOLERANCE = 24;

/**
 * Build a linear edge chain for nodes in array order:
 * nodes[i] -> nodes[i + 1] for every adjacent pair.
 */
export function buildChainEdges(nodes: AutomationNode[]): AutomationEdge[] {
  const edges: AutomationEdge[] = [];
  for (let i = 0; i < nodes.length - 1; i++) {
    edges.push({
      id: `edge_${nodes[i].id}_${nodes[i + 1].id}`,
      source: nodes[i].id,
      target: nodes[i + 1].id,
    });
  }
  return edges;
}

/**
 * Move one node to a new index position in the sequence, updating node x-positions
 * and re-building edge connections.
 */
export function moveNode(
  nodes: AutomationNode[],
  edges: AutomationEdge[],
  nodeId: string,
  targetIndex: number
): { nodes: AutomationNode[]; edges: AutomationEdge[] } {
  const fromIndex = nodes.findIndex((n) => n.id === nodeId);
  if (fromIndex === -1) return { nodes, edges };

  const clampedIndex = Math.max(0, Math.min(nodes.length - 1, targetIndex));
  if (clampedIndex === fromIndex) return { nodes, edges };

  const reordered = [...nodes];
  const [moved] = reordered.splice(fromIndex, 1);
  reordered.splice(clampedIndex, 0, moved);

  const resequenced = reordered.map((node, i) => ({
    ...node,
    position: {
      ...node.position,
      x: CANVAS_NODE_X_ORIGIN + i * CANVAS_NODE_X_STEP,
    },
  }));

  return { nodes: resequenced, edges: buildChainEdges(resequenced) };
}

/**
 * Auto Align — TIDY IN PLACE. A pure position transform anchored to the
 * user's own layout; it never re-lays the flow out from scratch.
 *
 * Guarantees (pinned by tests/automationAutoAlign.test.ts):
 * - STRUCTURE IDENTICAL: nodes keep ids, array order, type and data;
 *   only `position` changes. The incoming edge set is returned untouched
 *   (same array, byte-identical) — the Start (trigger) edge (source
 *   'start', whose node is not part of the nodes array) and every
 *   user-drawn edge survive exactly as drawn. Never rebuild edges here:
 *   a fabricated buildChainEdges() chain silently drops the Start edge
 *   and any non-adjacent connection. Gaps in the chain stay gaps.
 * - COLUMNS FROM THE EXECUTION SEQUENCE: nodes are ordered by
 *   computeNodeSequenceMap (the same BFS the engine's
 *   buildExecutionSequence runs, so layout order IS execution order,
 *   across gaps exactly as the stored edges define it) and placed at
 *   x = minCurrentX + sequenceIndex * CANVAS_NODE_X_STEP — one uniform
 *   pitch, anchored to the user's own left edge rather than a fixed
 *   origin the flow teleports to.
 * - LANES PRESERVED: current y values are clustered into lanes (a node
 *   joins the open lane while its top is within card height +
 *   CANVAS_LANE_SNAP_TOLERANCE of the lane's BASELINE — its minimum y —
 *   never of the lane's growing span, so intermediate nodes cannot
 *   chain-creep the lane downward) and every node in a lane snaps to
 *   the lane's minimum y. A node placed far below the chain stays on
 *   its own lower lane; nothing is flattened onto one row.
 * - NO OVERLAPS: same lane ⇒ same y with columns ≥ 280 apart > 250 card
 *   width; different lanes ⇒ baselines ≥ 144 apart > 120 card height.
 * - FIXED POINT: the sequence never depends on positions, the arranged
 *   minimum x equals the anchor, and snapped lane baselines are ≥
 *   CANVAS_NODE_HEIGHT + CANVAS_LANE_SNAP_TOLERANCE apart, so
 *   re-clustering re-forms the identical lanes. align(align(g)) is
 *   position-identical to align(g) — repeated clicks never drift.
 */
export function autoArrangeNodes(
  nodes: AutomationNode[],
  edges: AutomationEdge[]
): { nodes: AutomationNode[]; edges: AutomationEdge[] } {
  if (nodes.length === 0) return { nodes: [], edges };

  const xOf = (n: AutomationNode) =>
    typeof n.position?.x === 'number' ? n.position.x : CANVAS_NODE_X_ORIGIN;
  const yOf = (n: AutomationNode) =>
    typeof n.position?.y === 'number' ? n.position.y : CANVAS_NODE_Y_DEFAULT;

  // Columns: execution-sequence order, anchored at the user's left edge.
  const sequenceMap = computeNodeSequenceMap(nodes, edges);
  const bySequence = nodes
    .map((node, arrayIndex) => ({
      id: node.id,
      arrayIndex,
      seq: sequenceMap.get(node.id) ?? Number.MAX_SAFE_INTEGER,
    }))
    .sort((a, b) => a.seq - b.seq || a.arrayIndex - b.arrayIndex);
  const originX = Math.min(...nodes.map(xOf));
  const columnX = new Map<string, number>();
  bySequence.forEach((entry, column) => {
    columnX.set(entry.id, originX + column * CANVAS_NODE_X_STEP);
  });

  // Lanes: sweep by current y; a node joins the open lane while its top
  // is within card height + tolerance OF THE LANE'S BASELINE (laneMinY)
  // AND its column is not already occupied in that lane, else it opens
  // a new lane below. Every member snaps to the lane's minimum y.
  const byY = [...nodes].sort(
    (a, b) => yOf(a) - yOf(b) || xOf(a) - xOf(b) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)
  );
  const laneBaseline = new Map<string, number>();
  let laneMinY = 0;
  let laneOpen = false;
  const laneOccupiedCols = new Set<number>();

  for (const node of byY) {
    const y = yOf(node);
    const colX = columnX.get(node.id) ?? xOf(node);
    const colOccupied = laneOccupiedCols.has(colX);

    if (!laneOpen || y >= laneMinY + CANVAS_NODE_HEIGHT + CANVAS_LANE_SNAP_TOLERANCE || colOccupied) {
      laneMinY = laneOpen && colOccupied
        ? Math.max(y, laneMinY + CANVAS_NODE_HEIGHT + CANVAS_LANE_SNAP_TOLERANCE)
        : y;
      laneOpen = true;
      laneOccupiedCols.clear();
    }
    laneOccupiedCols.add(colX);
    laneBaseline.set(node.id, laneMinY);
  }

  const arrangedNodes = nodes.map((node) => ({
    ...node,
    position: {
      x: columnX.get(node.id) ?? xOf(node),
      y: laneBaseline.get(node.id) ?? yOf(node),
    },
  }));

  return { nodes: arrangedNodes, edges };
}

/**
 * Computes topological sequence step numbers (1, 2, 3...) for nodes.
 * Walks edges from root/start nodes down the edge graph.
 */
export function computeNodeSequenceMap(
  nodes: AutomationNode[],
  edges: AutomationEdge[]
): Map<string, number> {
  const map = new Map<string, number>();
  if (nodes.length === 0) return map;

  const nodeIds = new Set(nodes.map((n) => n.id));
  // An incoming target edge from ANOTHER action node in `nodes`
  const incomingActionTargets = new Set(
    (edges || [])
      .filter((e) => e.source !== 'start' && nodeIds.has(e.source) && nodeIds.has(e.target))
      .map((e) => e.target)
  );

  // Nodes with edges directly from 'start'
  const startTargets = nodes.filter(
    (n) => (edges || []).some((e) => e.source === 'start' && e.target === n.id)
  );

  // Action nodes that have NO incoming edge from any other action node
  const rootNodes = nodes.filter(
    (n) => n.type === 'start' || !incomingActionTargets.has(n.id)
  );

  let startList: AutomationNode[] = [];
  if (startTargets.length > 0) {
    const startTargetIds = new Set(startTargets.map((n) => n.id));
    const nonStartRoots = rootNodes.filter((n) => !startTargetIds.has(n.id));
    startList = [...startTargets, ...nonStartRoots];
  } else if (rootNodes.length > 0) {
    startList = rootNodes;
  } else {
    startList = [nodes[0]];
  }

  const visited = new Set<string>();
  let step = 1;

  const queue = [...startList];
  while (queue.length > 0) {
    const curr = queue.shift()!;
    if (visited.has(curr.id)) continue;
    visited.add(curr.id);

    if (curr.type !== 'start') {
      map.set(curr.id, step++);
    }

    const outgoing = (edges || []).filter((e) => e.source === curr.id);
    for (const edge of outgoing) {
      const nextNode = nodes.find((n) => n.id === edge.target);
      if (nextNode && !visited.has(nextNode.id)) {
        queue.push(nextNode);
      }
    }
  }

  // Any unvisited nodes
  for (const n of nodes) {
    if (!visited.has(n.id) && n.type !== 'start') {
      map.set(n.id, step++);
    }
  }

  return map;
}

/**
 * Check if adding edge source -> target would introduce a cycle.
 */
export function wouldCauseCycle(
  edges: AutomationEdge[],
  sourceId: string,
  targetId: string
): boolean {
  if (sourceId === targetId) return true;

  const visited = new Set<string>();
  const queue = [targetId];

  while (queue.length > 0) {
    const curr = queue.shift()!;
    if (curr === sourceId) return true;
    if (visited.has(curr)) continue;
    visited.add(curr);

    const outgoing = edges.filter((e) => e.source === curr);
    for (const edge of outgoing) {
      queue.push(edge.target);
    }
  }

  return false;
}

/**
 * Connect sourceId -> targetId by dragging canvas handle.
 * Enforces sequential workflow rules, replaces existing connections on target/source if needed,
 * and prevents self-loops and cycles.
 */
export function addConnection(
  nodes: AutomationNode[],
  edges: AutomationEdge[],
  sourceId: string,
  targetId: string
): { nodes: AutomationNode[]; edges: AutomationEdge[]; success: boolean; reason?: string } {
  if (sourceId === targetId) {
    return { nodes, edges, success: false, reason: 'Cannot connect a node to itself.' };
  }

  if (wouldCauseCycle(edges, sourceId, targetId)) {
    return { nodes, edges, success: false, reason: 'Connecting these nodes would create a circular loop.' };
  }

  // Remove existing outgoing edge from sourceId and incoming edge to targetId
  // (strictly maintaining sequential single-line execution flow)
  const filteredEdges = edges.filter(
    (e) => e.source !== sourceId && e.target !== targetId
  );

  const newEdge: AutomationEdge = {
    id: `edge_${sourceId}_${targetId}`,
    source: sourceId,
    target: targetId,
  };

  const newEdges = [...filteredEdges, newEdge];

  // Re-sort nodes in topological sequence order based on edges
  const sequenceMap = computeNodeSequenceMap(nodes, newEdges);
  const reorderedNodes = [...nodes].sort((a, b) => {
    const seqA = sequenceMap.get(a.id) ?? 999;
    const seqB = sequenceMap.get(b.id) ?? 999;
    return seqA - seqB;
  });

  return { nodes: reorderedNodes, edges: newEdges, success: true };
}

/**
 * Remove a connection line without deleting either node.
 */
export function removeConnection(
  nodes: AutomationNode[],
  edges: AutomationEdge[],
  edgeId: string
): { nodes: AutomationNode[]; edges: AutomationEdge[] } {
  const newEdges = edges.filter((e) => e.id !== edgeId);
  return { nodes, edges: newEdges };
}

/**
 * Delete a node and bridge its incoming predecessor directly to its outgoing successor
 * so the flow remains unbroken.
 */
export function deleteNodeAndReconnect(
  nodes: AutomationNode[],
  edges: AutomationEdge[],
  nodeId: string
): { nodes: AutomationNode[]; edges: AutomationEdge[] } {
  const incomingEdge = edges.find((e) => e.target === nodeId);
  const outgoingEdge = edges.find((e) => e.source === nodeId);

  const remainingNodes = nodes.filter((n) => n.id !== nodeId);
  let remainingEdges = edges.filter((e) => e.source !== nodeId && e.target !== nodeId);

  if (incomingEdge && outgoingEdge) {
    const bridgedEdge: AutomationEdge = {
      id: `edge_${incomingEdge.source}_${outgoingEdge.target}`,
      source: incomingEdge.source,
      target: outgoingEdge.target,
    };
    remainingEdges.push(bridgedEdge);
  }

  return { nodes: remainingNodes, edges: remainingEdges };
}

/**
 * Duplicate a selected action node and place it near the original,
 * maintaining clean visual position and data copy.
 */
export function duplicateNode(
  nodes: AutomationNode[],
  edges: AutomationEdge[],
  nodeId: string
): { nodes: AutomationNode[]; edges: AutomationEdge[]; newNodeId: string } {
  const sourceNode = nodes.find((n) => n.id === nodeId);
  if (!sourceNode) return { nodes, edges, newNodeId: '' };

  const newNodeId = `node_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
  const sourceIndex = nodes.findIndex((n) => n.id === nodeId);

  const newNode: AutomationNode = {
    id: newNodeId,
    type: 'action',
    position: {
      x: sourceNode.position.x + 280,
      y: sourceNode.position.y + 40,
    },
    data: {
      ...JSON.parse(JSON.stringify(sourceNode.data)),
      label: `${sourceNode.data.label} (Copy)`,
    },
  };

  const newNodes = [...nodes];
  newNodes.splice(sourceIndex + 1, 0, newNode);

  // Re-index X positions for all subsequent nodes if needed
  const resequencedNodes = newNodes.map((n, idx) => ({
    ...n,
    position: {
      ...n.position,
      x: idx > sourceIndex + 1 ? n.position.x + 280 : n.position.x,
    },
  }));

  const newEdges = buildChainEdges(resequencedNodes);

  return { nodes: resequencedNodes, edges: newEdges, newNodeId };
}

/**
 * Format a clear, short summary text string for an action node based on its type and config.
 */
export function generateNodeSummary(data: AutomationNodeData): string {
  const { actionType, config } = data;
  if (!config) return 'Not configured';

  switch (actionType as AutomationActionType) {
    case 'generate_document':
      if (config.templateName) return `Doc: ${config.templateName}`;
      if (config.templateId) return `Doc Template ID: ${config.templateId}`;
      return 'Generates applicant PDF';

    case 'send_sign_request':
      if (config.signingTemplateName) return `Sign Request: ${config.signingTemplateName}`;
      if (config.signingTemplateId) return `Sign Template ID: ${config.signingTemplateId}`;
      return 'Sends e-signature request';

    case 'send_email':
      if (config.subject) return `Email: "${config.subject}"`;
      if (config.emailTemplateId) return `Template: ${config.emailTemplateId}`;
      return 'Sends email notification';

    case 'create_event':
      if (config.eventTitle) return `Event: ${config.eventTitle}`;
      return 'Schedules calendar event';

    case 'create_invoice':
      if (config.invoiceTitle) return `Invoice: ${config.invoiceTitle}`;
      if (config.invoiceTemplateId) return 'Invoice from template';
      return 'Generates payment invoice';

    case 'create_quote':
      if (config.quoteTitle) return `Quote: ${config.quoteTitle}`;
      if (config.quoteTemplateId) return 'Quote from template';
      return 'Generates formal quote';

    case 'create_support_ticket':
      if (config.ticketTitle) return `Ticket: ${config.ticketTitle}`;
      return 'Raises support ticket';

    case 'create_internal_task':
      if (config.taskTitle) return `Task: ${config.taskTitle}`;
      return 'Creates internal team task';

    case 'tag_application': {
      const added = Array.isArray(config.tagsToAdd)
        ? config.tagsToAdd.join(', ')
        : typeof config.tagsToAdd === 'string'
          ? config.tagsToAdd
          : '';
      const removed = Array.isArray(config.tagsToRemove)
        ? config.tagsToRemove.join(', ')
        : typeof config.tagsToRemove === 'string'
          ? config.tagsToRemove
          : '';
      if (added && removed) return `Add: ${added} | Remove: ${removed}`;
      if (added) return `Add Tag: ${added}`;
      if (removed) return `Remove Tag: ${removed}`;
      return 'Modifies application tags';
    }

    case 'change_workflow_stage':
      if (config.targetStageLabel) return `Stage → ${config.targetStageLabel}`;
      if (config.targetStageKey) return `Stage → ${config.targetStageKey}`;
      return 'Advances workflow stage';

    case 'update_application_status':
      if (config.targetApplicationStatus) return `Status → ${config.targetApplicationStatus}`;
      return 'Updates application status';

    case 'add_to_batch':
      if (config.batchName) return `Batch: ${config.batchName}`;
      return 'Adds to cohort batch';

    case 'update_batch':
      if (config.batchOperation === 'replace_all') {
        return config.batchName
          ? `Replaces all members of batch: ${config.batchName}`
          : 'Replaces all batch members with the selected applicants';
      }
      if (config.batchOperation === 'remove_member') {
        // Remove Member is workspace-wide (D-4): it names no batch.
        return 'Removes the applicant from every batch they belong to';
      }
      if (config.batchName) return `Batch: ${config.batchName}`;
      return 'Adds to cohort batch';

    case 'update_admin': {
      const who = config.adminTargetName || config.adminTargetUid || 'a workspace admin';
      if (config.adminOperation === 'remove_admin') return `Removes workspace admin: ${who}`;
      if (config.adminOperation === 'append') return `Adds ${who} to screening staff`;
      if (config.adminField === 'role') return `Sets ${who}'s role → ${config.adminValue || '—'}`;
      if (config.adminField === 'status') return `Sets ${who}'s status → ${config.adminValue || '—'}`;
      if (config.adminField === 'screeningStaff') {
        return config.adminValue === false ? `Unassigns ${who} as screening staff` : `Assigns ${who} as screening staff`;
      }
      if (config.adminField) return `Sets ${who}'s ${config.adminField}`;
      return 'Updates a workspace team administrator';
    }

    default:
      return 'Action step';
  }
}
